'use client';

import { createContext, useState, useEffect, useMemo } from 'react';
import type { Dispatch, ReactNode, SetStateAction } from 'react';

import { LOYALTY_LOCAL_STORAGE_KEY, type Nullable } from '@libs/core';
import type { LoyaltyMemberPoints, Offer, PointsModel } from '@libs/common-types';

import { filterOffersByType } from '../utils';
import { useLocalStorage } from '../hooks/useLocalStorage';
import { useUserSession } from '../hooks/useUserSession';
import { getOffers, getMemberPoints, getPointsHistory } from '@libs/api-proxy-client/v2';
import { SnackbarUtils } from '@libs/shared-ui/utils';

export interface LoyaltyContextProps {
    drawerOpen: boolean;
    forceDataUpdate: boolean;
    featuredOffers: Nullable<Offer[]>;
    getOffers: () => Promise<void>;
    isActivating: boolean;
    memberPoints?: Nullable<LoyaltyMemberPoints>;
    isLoading?: boolean;
    offers: Nullable<Offer[]>;
    pointsHistory?: Nullable<PointsModel[]>;
    selectedOffer?: Offer;
    setDrawerOpen: Dispatch<SetStateAction<boolean>>;
    setForceDataUpdate: Dispatch<SetStateAction<boolean>>;
    setIsActivating: Dispatch<SetStateAction<boolean>>;
    setSelectedOffer: Dispatch<SetStateAction<Offer | undefined>>;
    setPointsHistory?: (history: Nullable<PointsModel[]>) => void;
}

export type StorageLoyaltySession = {
    offers: Nullable<Offer[]>;
    featuredOffers: Nullable<Offer[]>;
    memberPoints: Nullable<LoyaltyMemberPoints>;
    pointsHistory: Nullable<PointsModel[]>;
};

export const initialLoyaltyState: LoyaltyContextProps = {
    drawerOpen: false,
    featuredOffers: undefined,
    getOffers: async () => undefined,
    isActivating: false,
    memberPoints: undefined,
    offers: undefined,
    pointsHistory: undefined,
    selectedOffer: undefined,
    setDrawerOpen: () => undefined,
    setIsActivating: () => undefined,
    setSelectedOffer: () => undefined,
    setPointsHistory: undefined,
    forceDataUpdate: false,
    setForceDataUpdate: () => undefined,
};

export const LoyaltyContext = createContext<Nullable<LoyaltyContextProps>>(undefined);

export function LoyaltyProvider({ children }: { children: ReactNode }) {
    const { user } = useUserSession();
    const [storageLoyaltyData, setStorageLoyaltyData] =
        useLocalStorage<StorageLoyaltySession>(LOYALTY_LOCAL_STORAGE_KEY);

    const [offers, setOffers] = useState<Nullable<Offer[]>>(storageLoyaltyData?.offers || undefined);
    const [featuredOffers, setFeaturedOffers] = useState<Nullable<Offer[]>>(
        storageLoyaltyData?.featuredOffers || undefined
    );
    const [memberPoints, setMemberPoints] = useState<Nullable<LoyaltyMemberPoints>>(
        storageLoyaltyData?.memberPoints || undefined
    );
    const [isLoading, setIsLoading] = useState(false);
    const [pointsHistory, setPointsHistory] = useState<Nullable<PointsModel[]>>(
        storageLoyaltyData?.pointsHistory || undefined
    );

    const [drawerOpen, setDrawerOpen] = useState(false);
    const [isActivating, setIsActivating] = useState(false);
    const [forceDataUpdate, setForceDataUpdate] = useState(false);
    const [selectedOffer, setSelectedOffer] = useState<Offer>();

    const setMemberPointsData = async () => {
        try {
            setIsLoading(true);
            const response = await getMemberPoints();

            if (response instanceof Error || (response && 'parsedResponse' in response)) {
                throw response;
            }

            setMemberPoints(response);
            setStorageLoyaltyData(prev => ({
                offers: prev?.offers || null,
                featuredOffers: prev?.featuredOffers || null,
                pointsHistory: prev?.pointsHistory || null,
                memberPoints: response || null,
            }));
            setIsLoading(false);
        } catch {
            SnackbarUtils.showPredefined('SERVER_ERROR');
            setIsLoading(false);
        }
    };

    const setPointsHistoryData = async () => {
        try {
            const response = await getPointsHistory();

            if (response instanceof Error || (response && 'parsedResponse' in response)) {
                throw response;
            }

            setPointsHistory(response);
            setStorageLoyaltyData(prev => ({
                offers: prev?.offers || null,
                featuredOffers: prev?.featuredOffers || null,
                memberPoints: prev?.memberPoints || null,
                pointsHistory: response || null,
            }));
        } catch {
            SnackbarUtils.showPredefined('SERVER_ERROR');
        }
    };

    const setOffersData = async () => {
        try {
            const response = await getOffers();

            if (response instanceof Error || (response && 'parsedResponse' in response)) {
                throw response;
            }

            const responseOffers = filterOffersByType({
                offers: response?.offers,
            });
            const responseFeaturedOffers = filterOffersByType({
                offers: response?.offers,
                type: 'Featured Offer',
            });

            setOffers(responseOffers);
            setFeaturedOffers(responseFeaturedOffers);
            setStorageLoyaltyData(prev => ({
                ...prev,
                offers: responseOffers || null,
                featuredOffers: responseFeaturedOffers || null,
                memberPoints: prev?.memberPoints || null,
                pointsHistory: prev?.pointsHistory || null,
            }));
        } catch {
            SnackbarUtils.showPredefined('SERVER_ERROR');
        }
    };

    useEffect(() => {
        if (forceDataUpdate) {
            setPointsHistoryData();
            setForceDataUpdate(false);
        }
    }, [forceDataUpdate]);

    useEffect(() => {
        // Initiate all requests in parallel
        setOffersData();
        setMemberPointsData();
        setPointsHistoryData();
    }, [user]);

    const contextValue = useMemo(
        () => ({
            drawerOpen,
            featuredOffers,
            forceDataUpdate,
            isActivating,
            memberPoints,
            offers,
            getOffers: setOffersData,
            pointsHistory,
            selectedOffer,
            isLoading,
            setDrawerOpen,
            setIsActivating,
            setSelectedOffer,
            setPointsHistory,
            setForceDataUpdate,
        }),
        [
            drawerOpen,
            featuredOffers,
            forceDataUpdate,
            isActivating,
            memberPoints,
            offers,
            pointsHistory,
            selectedOffer,
            isLoading,
            setDrawerOpen,
            setIsActivating,
            setSelectedOffer,
            setPointsHistory,
            setForceDataUpdate,
        ]
    );

    return <LoyaltyContext.Provider value={contextValue}>{children}</LoyaltyContext.Provider>;
}
